import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';

// Full-screen chords/lyrics overlay, shared between the Live Requests teleprompter
// and the Set List Builder's per-song lyrics lookup. `songKey` should change whenever
// `song` changes (e.g. on next/prev) so the chords/lyrics tab resets appropriately.
function TeleprompterOverlay({ song, songKey, onClose, onPrev, onNext, onComplete, completeLabel = '✓ Mark Played' }) {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(song.chords ? 'chords' : 'lyrics');

  useEffect(() => {
    setActiveTab(song.chords ? 'chords' : 'lyrics');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [songKey]);

  const hasFooter = Boolean(onPrev || onNext || onComplete);

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: '#0d0d0d', display: 'flex', flexDirection: 'column' }}>
      <div style={{ flexShrink: 0, padding: '0.75rem 1rem', background: '#1a1a1a', borderBottom: '1px solid #333', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        {song.chords && song.lyrics && (
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            {[['chords', 'Chords'], ['lyrics', 'Lyrics']].map(([key, label]) => (
              <button key={key} onClick={() => setActiveTab(key)} style={{
                padding: '0.35rem 0.85rem', border: 'none', borderRadius: '6px', cursor: 'pointer',
                fontSize: '0.85rem', fontWeight: '600',
                background: activeTab === key ? theme.primary : '#333',
                color: activeTab === key ? theme.onPrimary : '#aaa',
              }}>
                {label}
              </button>
            ))}
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: '700', color: 'white', fontSize: '0.95rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {song.title}
          </div>
          <div style={{ fontSize: '0.78rem', color: '#888', marginTop: '1px' }}>{song.artist_original}</div>
        </div>
        <button onClick={onClose} style={{
          background: '#333', border: 'none', borderRadius: '8px', color: 'white',
          padding: '0.45rem 0.9rem', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '600', flexShrink: 0,
        }}>
          ✕ Close
        </button>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: '1.25rem 1rem' }}>
        {activeTab === 'chords' && song.chords && (
          <pre style={{ color: '#e8e8e8', fontFamily: 'monospace', fontSize: '1.05rem', lineHeight: 1.9, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
            {song.chords}
          </pre>
        )}
        {activeTab === 'lyrics' && song.lyrics && (
          <pre style={{ color: '#e8e8e8', fontFamily: "'Helvetica Neue', Arial, sans-serif", fontSize: '1.1rem', lineHeight: 2, whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>
            {song.lyrics}
          </pre>
        )}
        {activeTab === 'chords' && !song.chords && song.lyrics && (
          <pre style={{ color: '#e8e8e8', fontFamily: 'sans-serif', fontSize: '1.1rem', lineHeight: 2, whiteSpace: 'pre-wrap', margin: 0 }}>
            {song.lyrics}
          </pre>
        )}
        {!song.chords && !song.lyrics && (
          <div style={{ color: '#888', fontSize: '0.95rem', textAlign: 'center', marginTop: '3rem' }}>
            No lyrics or chords added for this song yet.
          </div>
        )}
      </div>
      {hasFooter && (
        <div style={{ flexShrink: 0, padding: '0.65rem 1rem', background: '#1a1a1a', borderTop: '1px solid #333', display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
          <button onClick={onPrev} disabled={!onPrev} style={{
            display: 'flex', alignItems: 'center', gap: '0.3rem',
            background: '#333', color: onPrev ? 'white' : '#555', border: 'none', borderRadius: '8px',
            padding: '0.6rem 0.9rem', cursor: onPrev ? 'pointer' : 'default', fontSize: '0.85rem', fontWeight: '600',
          }}>
            <ChevronLeft size={16} /> Prev
          </button>
          {onComplete ? (
            <button onClick={onComplete} style={{
              flex: 1, background: theme.primary, color: theme.onPrimary, border: 'none', borderRadius: '8px',
              padding: '0.7rem 1rem', cursor: 'pointer', fontSize: '0.9rem', fontWeight: '700',
            }}>
              {completeLabel}
            </button>
          ) : <div style={{ flex: 1 }} />}
          <button onClick={onNext} disabled={!onNext} style={{
            display: 'flex', alignItems: 'center', gap: '0.3rem',
            background: '#333', color: onNext ? 'white' : '#555', border: 'none', borderRadius: '8px',
            padding: '0.6rem 0.9rem', cursor: onNext ? 'pointer' : 'default', fontSize: '0.85rem', fontWeight: '600',
          }}>
            Next <ChevronRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}

export default TeleprompterOverlay;
